import React, { useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import { Spinner, Input } from '@material-tailwind/react'
import { GoSearch } from 'react-icons/go'
import { useTranslation } from 'react-i18next'
import { getGeocoding } from '../utils/helpers/mapbox.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

interface PositionSuggestionProps {
  suggestionInput: string
  setSuggestionInput: (value: string) => void
  onSuggestionSelect: (suggestion: { place_name: string; center: [number, number] }) => void
}

const PositionSuggestion: React.FC<PositionSuggestionProps> = ({
  suggestionInput,
  setSuggestionInput,
  onSuggestionSelect,
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<
    Array<{ id?: string; place_name: string; center: [number, number] }>
  >([])
  const { darkMode } = useDarkMode()

  const geocode = async (value: string) => {
    if (value === '' || value.length < 3) {
      setSuggestions([])
      setLoading(false)
      return
    }

    setLoading(true)

    const { data, error } = await getGeocoding(value)

    if (error) {
      setLoading(false)
      return
    }

    setLoading(false)

    setSuggestions(data?.features || [])
  }

  const selectSuggestion = (suggestion: { place_name: string; center: [number, number] }) => {
    setSuggestions([])
    setSuggestionInput(suggestion.place_name)
    onSuggestionSelect(suggestion)
  }

  const clearSuggestion = () => {
    setSuggestions([])
    setLoading(false)
  }

  return (
    <div className="w-full relative">
      <div className="relative h-[41px]">
        <Input
          className="w-full"
          label={t('upload.position.searchPlace')}
          value={suggestionInput}
          onChange={(e) => {
            // set value
            setSuggestionInput(e.currentTarget.value)
            // perform geocoding to load suggestions
            geocode(e.currentTarget.value)
          }}
          icon={!loading && <GoSearch className="h-5 w-5" />}
          data-testid="agami-geo-pos-search-input"
          {...(darkMode && { color: 'white' })}
        />
        {loading && <Spinner className="absolute h-5 w-5 top-3 right-2" />}
      </div>

      {suggestions?.length > 0 && (
        <ClickAwayListener onClickAway={clearSuggestion}>
          <div className="absolute top-[calc(40px_+_1rem)] w-full border border-white dark:border-primary-600 flex flex-col shadow z-[99] rounded">
            {suggestions?.map((suggestion, idx) => {
              const bgColor =
                idx % 2 === 0
                  ? 'bg-white hover:bg-gray-100 dark:bg-primary-400 dark:hover:bg-primary-300'
                  : 'bg-base-gray hover:bg-base-gray-600 dark:bg-primary-500 dark:hover:bg-primary-300'
              return (
                <span
                  key={suggestion.id}
                  className={`inline-block w-full px-2 py-3 cursor-pointer ${bgColor}`}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  {suggestion.place_name}
                </span>
              )
            })}
          </div>
        </ClickAwayListener>
      )}
    </div>
  )
}

export default PositionSuggestion
